import {
  FileAttachableType,
  FileAttachment_Read,
  FileAttachment_Read_Nested,
  FileAttachmentCategory,
  Principal_Read_Nested,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';

import { Nullable } from '~types/Nullable';

export class FileAttachment {
  public static parse(proto: FileAttachment_Read): FileAttachment {
    return new FileAttachment(
      dayjs(proto.created_at),
      dayjs(proto.updated_at),
      proto.category as FileAttachmentCategory,
      proto.description ?? null,
      proto.file_attachable_id,
      proto.file_attachable_type,
      proto.file_url,
      proto.file_name,
      proto.id,
      proto.mime_type,
      proto.uploaded_by,
      proto.editable,
      proto.deletable,
      proto.preview_url,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }

  public get createdAt(): Dayjs {
    return this._created_at;
  }

  public get updatedAt(): Dayjs {
    return this._updated_at;
  }

  public get category(): FileAttachmentCategory {
    return this._category;
  }

  public get description(): Nullable<string> {
    return this._description;
  }

  public get fileAttachableId(): string {
    return this._file_attachable_id;
  }

  public get fileAttachableType(): FileAttachableType {
    return this._file_attachable_type;
  }

  public get fileUrl(): string {
    return this._file_url;
  }

  public get fileName(): string {
    return this._file_name;
  }

  public get id(): string {
    return this._id;
  }

  public get mimeType(): string {
    return this._mime_type;
  }

  public get uploadedBy(): Principal_Read_Nested {
    return this._uploaded_by;
  }

  public get editable(): boolean {
    return this._editable;
  }

  public get deletable(): boolean {
    return this._deletable;
  }

  public get previewUrl() {
    return this._preview_url || this._file_url;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  constructor(
    public _created_at: Dayjs,
    public _updated_at: Dayjs,
    public _category: FileAttachmentCategory,
    public _description: Nullable<string>,
    public _file_attachable_id: string,
    public _file_attachable_type: FileAttachableType,
    public _file_url: string,
    public _file_name: string,
    public _id: string,
    public _mime_type: string,
    public _uploaded_by: Principal_Read_Nested,
    public _editable: boolean,
    public _deletable: boolean,
    public _preview_url: Nullable<string>,
    public _discarded_at: Dayjs | null = null,
  ) {}
}

export class FileAttachmentBasic {
  public static parse(proto: FileAttachment_Read_Nested) {
    return new FileAttachmentBasic(
      proto.id,
      proto.file_name,
      proto.file_url,
      proto.mime_type,
      proto.byte_size,
      proto.preview_url ?? null,
      proto.preview_mime_type ?? null,
      proto.download_url,
    );
  }

  public get id() {
    return this._id;
  }

  public get fileName() {
    return this._file_name;
  }

  public get fileUrl() {
    return this._file_url;
  }

  public get downloadUrl() {
    return this._download_url;
  }

  public get mimeType() {
    return this._mime_type;
  }

  public get byteSize() {
    return this._byte_size;
  }

  public get previewMimeType() {
    return this._preview_mime_type;
  }

  public get isImage() {
    return this._mime_type.startsWith('image/');
  }

  /**
   * Previews are generated on a background queue, so a freshly uploaded image has no
   * preview yet; the full file stands in until the transform lands.
   */
  public get previewUrl() {
    return this._preview_url ?? this._file_url;
  }

  constructor(
    private _id: string,
    private _file_name: string,
    private _file_url: string,
    private _mime_type: string,
    private _byte_size: number,
    private _preview_url: Nullable<string>,
    private _preview_mime_type: Nullable<string>,
    private _download_url: Nullable<string>,
  ) {}
}
